import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Award, ArrowRight, MessageCircle } from 'lucide-react';
import { cscsCards, heroImages, companyInfo } from '../data/mock';

const CSCSServicesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={heroImages.alt1} 
            alt="CSCS Card Services" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              CSCS Card Services
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Get your Construction Skills Certification Scheme card and start working on UK construction sites
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              Book Your CSCS Test
            </a>
          </div>
        </div>
      </section>

      {/* What is CSCS */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-6">
              What is a CSCS Card?
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                The CSCS (Construction Skills Certification Scheme) card is the industry-standard certification for construction workers in the UK. It proves that you have the required training and qualifications for your specific role on a construction site.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Most UK construction sites require all workers to hold a valid CSCS card before being allowed on site.</strong> Without one, you risk being turned away and losing work opportunities.
              </p>
              
              <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg my-8">
                <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                  Why You Need a CSCS Card:
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Required by most UK construction sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Proves your competence and qualifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Demonstrates health & safety knowledge</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Opens up more job opportunities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <span>Industry-recognized across the UK</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSCS Card Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              Types of CSCS Cards
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the right CSCS card for your experience level and trade
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {cscsCards.map((card, index) => (
              <div
                key={index}
                id={card.type.toLowerCase().split(' ')[0]}
                className={`bg-white rounded-xl p-8 border-2 ${
                  card.popular 
                    ? 'border-construction-yellow shadow-xl' 
                    : 'border-gray-200 shadow-sm'
                } hover:shadow-xl transition-all relative`}
              >
                {card.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-construction-yellow text-construction-darkblue px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-construction-darkblue mb-2">
                    {card.type}
                  </h3>
                  <p className="text-construction-yellow font-semibold text-lg">
                    {card.level}
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <Award className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Requirement:</p>
                      <p className="font-semibold text-construction-darkblue">{card.requirement}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="text-construction-yellow flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Duration:</p>
                      <p className="font-semibold text-construction-darkblue">{card.duration}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <p className="text-3xl font-bold text-construction-darkblue mb-4">
                    {card.price}
                  </p>
                  <ul className="space-y-2">
                    {card.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={`tel:${companyInfo.phone}`}
                  onClick={(e) => {
                    // Track the click
                    console.log(`Apply Now clicked for: ${card.type}`);
                  }}
                  className={`block text-center px-6 py-3 rounded-lg font-bold transition-all hover:scale-105 ${
                    card.popular
                      ? 'bg-construction-yellow text-construction-darkblue hover:bg-yellow-500'
                      : 'bg-construction-darkblue text-white hover:bg-black'
                  }`}
                >
                  Call to Apply Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSCS Test Booking */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-6">
              CSCS Test Booking Service
            </h2>
            <div className="bg-gray-50 rounded-xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We offer fast and convenient CSCS test booking services. Our team will help you find the nearest test center and book your slot at a time that suits you.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-construction-darkblue mb-3 text-lg">
                    Labourer Test (Touchscreen)
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>45 minutes duration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>50 multiple choice questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>Available in 40+ languages</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>Results available immediately</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="font-bold text-construction-darkblue mb-3 text-lg">
                    Operative/Specialist Test
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>45 minutes duration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>Trade-specific questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>Core health & safety</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                      <span>Pass mark: 45 out of 50</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-construction-yellow/20 border-l-4 border-construction-yellow p-6 rounded-r-lg">
                <h3 className="font-bold text-construction-darkblue mb-3">
                  Free Test Preparation Materials
                </h3>
                <p className="text-gray-700">
                  When you book through us, we provide free access to test preparation materials and practice questions to help you pass first time!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Your CSCS Card?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Call us now or fill out our online form to get started
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in CSCS cards`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105"
            >
              Apply Online
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CSCSServicesPage;
